import React from 'react';
import { Box, styled } from '@mui/material';
import {
  LpFeeCalculationBasis,
  LpFeeCommitmentStatus,
} from 'further-types/lp/lp-fee-template';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import {
  CalendarDay,
  CalendarTime,
  numberToCurrencyString,
} from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import FeesPreviewAccordion from './FeesPreviewAccordion';

type FeeDetailsForCapitalAction = {
  _id: string;
  feeActionId: string;
  limitedPartner: string;
  feeActionCreatedAt: CalendarTime;
  accrualDate: CalendarDay;
  lpFeeTemplateName: string;
  lpFeeTemplateFeeName: string;
  insideOutsideCommitment: LpFeeCommitmentStatus;
  calculationBasis: LpFeeCalculationBasis;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
};

type FeeAction = {
  id: string;
  accrualDate: CalendarDay;
  feeActionCreatedAt: CalendarTime;
  investorCount: number;
  amountIncVatLcy: number;
  amountExVatLcy: number;
  salesTaxLcy: number;
  fees: Array<FeeDetailsForCapitalAction>;
};

type Props = {
  baseCurrency: CurrencyCode;
  feeActions: Array<FeeAction>;
};

const FeesWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const FeeDetails: React.FC<Props> = ({ feeActions, baseCurrency }) => {
  const { expandedRows, toggleRowExpansion } = useExpandedRows([]);

  const columns: Array<TableColumn<FeeAction>> = [
    {
      key: 'accrualDate',
      label: 'Accrual date',
      render: (feeAction) => (
        <ColorIndicatorWithText
          text={
            feeAction.fees[0]?.lpFeeTemplateFeeName ??
            feeAction.accrualDate.toReadable()
          }
          colorKey={{
            name:
              feeAction.fees[0]?.lpFeeTemplateFeeName ??
              feeAction.accrualDate.toLabel(),
          }}
        />
      ),
    },
    {
      key: 'feeActionCreatedAt',
      label: 'Approval date',
      render: (feeAction) => feeAction.feeActionCreatedAt.toReadable(),
    },
    {
      key: 'amountExVatLcy',
      label: 'Amount (ex sales tax)',
      render: (feeAction) =>
        numberToCurrencyString(feeAction.amountExVatLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'salesTaxLcy',
      label: 'Sales tax',
      render: (feeAction) =>
        numberToCurrencyString(feeAction.salesTaxLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'amountIncVatLcy',
      label: 'Amount (inc sales tax)',
      render: (feeAction) =>
        numberToCurrencyString(feeAction.amountIncVatLcy, {
          currency: baseCurrency,
        }),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={feeActions}
      isExpandableRow={() => true}
      isExpandedRow={({ id }) => expandedRows.has(id)}
      onRowExpand={({ id }) => toggleRowExpansion(id)}
      onRowClick={({ id }) => toggleRowExpansion(id)}
      getExpandedContent={(feeAction) => (
        <FeesWrapper>
          <FeesPreviewAccordion fees={feeAction.fees} />
        </FeesWrapper>
      )}
    />
  );
};

export default FeeDetails;
